import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Play, 
  Trash2, 
  ArrowLeft, 
  Calendar,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSubscription } from '../context/SubscriptionContext';
import UpgradeModal from '../components/UpgradeModal';
import { toast } from 'sonner';
import './WheelGameHub.css';

const WheelGameHub = () => {
  const navigate = useNavigate();
  const { trackPlay, isPremium, canPlayMore, playedGamesCount, maxPlayedGames } = useSubscription();
  const [savedWheels, setSavedWheels] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const loadSavedWheels = () => {
    try {
      const data = localStorage.getItem('wheelDatabase');
      const wheels = data ? JSON.parse(data) : [];
      console.log('📂 Loaded wheels:', wheels);
      setSavedWheels(wheels);
    } catch (error) {
      console.error('Error loading saved wheels:', error);
      toast.error('Failed to load saved wheels');
    }
  };

  useEffect(() => {
    loadSavedWheels();
  }, []);

  const createNewWheel = () => {
    navigate('/wheel-game');
  };

  const playWheel = async (wheelId) => {
    // Check play limits for free users
    if (!isPremium && !canPlayMore) {
      setShowUpgradeModal(true);
      return;
    }

    // Track the play
    const result = await trackPlay(`wheel-${wheelId}`, 'wheel');
    if (!result.success && result.limitReached) {
      setShowUpgradeModal(true);
      return;
    }

    navigate(`/wheel-game?wheelId=${wheelId}`);
  };

  const deleteWheel = (wheelId) => {
    try {
      const data = localStorage.getItem('wheelDatabase');
      const wheels = data ? JSON.parse(data) : [];
      const filteredWheels = wheels.filter(w => w.id !== wheelId);
      localStorage.setItem('wheelDatabase', JSON.stringify(filteredWheels));
      
      loadSavedWheels();
      setShowDeleteConfirm(null);
      toast.success('Wheel deleted successfully');
    } catch (error) {
      console.error('Error deleting wheel:', error);
      toast.error('Failed to delete wheel');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const isPlayDisabled = !isPremium && !canPlayMore;

  return (
    <div className="wheel-hub-page">
      <div className="wheel-hub-container">
        <div className="hub-header">
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="back-button"
            variant="ghost"
          >
            <ArrowLeft size={20} />
            Back
          </Button>
          <div className="hub-title">
            <div className="title-with-icon">
              <div className="hub-icon">🎡</div>
              <div>
                <h1>Lucky Learning Wheel</h1>
                <p>Spin to learn and win!</p>
              </div>
            </div>
          </div>
        </div>

        <motion.div
          className="create-new-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button onClick={createNewWheel} className="create-new-btn">
            <Plus size={24} />
            Create New Game
          </Button>
        </motion.div>

        <div className="wheels-grid">
          {savedWheels.map((wheel, index) => (
              <motion.div
                key={wheel.id}
                className="wheel-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="wheel-card-header">
                  <div className="wheel-icon">🎡</div>
                  <div className="wheel-info">
                    <h3 className="wheel-title">{wheel.title || 'Untitled Wheel'}</h3>
                    <div className="wheel-meta">
                      <span className="meta-item">
                        <Calendar size={14} />
                        {formatDate(wheel.createdAt)}
                      </span>
                      {wheel.createdAt && (
                        <span className="meta-item">
                          <Clock size={14} />
                          {formatTime(wheel.createdAt)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="wheel-details">
                  <p className="wheel-slices">🎯 {wheel.slices?.length || 0} slices</p>
                </div>

                <div className="wheel-card-actions">
                  <Button 
                    onClick={() => playWheel(wheel.id)} 
                    className={`play-btn ${isPlayDisabled ? 'disabled' : ''}`}
                    disabled={isPlayDisabled}
                  >
                    <Play size={18} />
                    {isPlayDisabled ? 'Limit Reached' : 'Play'}
                  </Button>
                  <Button 
                    onClick={() => setShowDeleteConfirm(wheel.id)} 
                    className="delete-btn"
                    variant="ghost"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            className="delete-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              className="delete-dialog"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Delete Wheel?</h3>
              <p>This action cannot be undone.</p>
              <div className="delete-actions">
                <Button 
                  onClick={() => setShowDeleteConfirm(null)} 
                  variant="ghost"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => deleteWheel(showDeleteConfirm)} 
                  className="confirm-delete-btn"
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType="play"
        currentCount={playedGamesCount}
        maxCount={maxPlayedGames || 3}
      />
    </div>
  );
};

export default WheelGameHub;
